import { QRAGraphType } from 'features/RiskManagement/Components/Graphs'
import type {
  IQRARiskForecastChartObject,
  IQRAThreatChartGrouping,
  IQRAThreatChartGroupingConfig,
  IQRAThreatChartValue,
  QRARiskForecastChartItems
} from 'features/RiskManagement/Components/Graphs'

interface IQRARiskForecastGraphCategoryResult {
  key: string
  label: string
  sortOrder: number
  values: IQRAThreatChartValue[]
}

type IQRARiskForecastGraphGroupingConfigResult = IQRAThreatChartGroupingConfig

export interface IQRARiskForecastGraphYearResult {
  year: number
  categoriesResult: IQRARiskForecastGraphCategoryResult[]
}

export interface IQRARiskForecastGraphApiResult {
  categoriesForecastResult: IQRARiskForecastGraphYearResult[]
  groupingConfigResult: IQRARiskForecastGraphGroupingConfigResult[]
}

export interface IQRARiskForecastGraphData {
  chart: IQRARiskForecastChartObject
  groupingConfig: IQRARiskForecastGraphGroupingConfigResult[]
}

const sortThreatGraphCategories = (
  categoriesResult: IQRARiskForecastGraphCategoryResult[]
) =>
  [...categoriesResult].sort((left, right) => left.sortOrder - right.sortOrder)

const getRiskForecastStateKeys = (
  categories: IQRARiskForecastGraphCategoryResult[]
) => {
  const stateKeys = new Set<string>()

  for (const category of categories) {
    for (const categoryValue of category.values) {
      stateKeys.add(categoryValue.key)
    }
  }

  return [...stateKeys].sort((left, right) => {
    if (left === 'baseline') {
      return -1
    }

    if (right === 'baseline') {
      return 1
    }

    return left.localeCompare(right)
  })
}

const getRiskForecastCategoryValue = (
  category: IQRARiskForecastGraphCategoryResult | undefined,
  stateKey: string
) => {
  const value = category?.values.find(
    (categoryValue) => categoryValue.key === stateKey
  )?.value

  return value ?? 0
}

const getRiskForecastGroupingValue = (
  groupingItem: IQRAThreatChartGrouping,
  stateKey: string,
  categoriesByKey: Map<string, IQRARiskForecastGraphCategoryResult>
) =>
  groupingItem.members.reduce((total, memberKey) => {
    const category = categoriesByKey.get(memberKey)

    return total + getRiskForecastCategoryValue(category, stateKey)
  }, 0)

const getRiskForecastItems = (
  categoriesResult: IQRARiskForecastGraphCategoryResult[],
  grouping?: IQRARiskForecastGraphGroupingConfigResult
): QRARiskForecastChartItems['items'] => {
  const categories = sortThreatGraphCategories(categoriesResult)

  if (grouping == null || grouping.groupings.length < 1) {
    return categories.map((category) => ({
      name: category.label,
      values: category.values
    }))
  }

  const categoriesByKey = new Map(
    categories.map((category) => [category.key, category])
  )

  const stateKeys = getRiskForecastStateKeys(categories)

  return grouping.groupings.map((groupingItem) => ({
    name: groupingItem.label,
    values: stateKeys.map((stateKey) => ({
      key: stateKey,
      value: getRiskForecastGroupingValue(
        groupingItem,
        stateKey,
        categoriesByKey
      )
    })),
    members: groupingItem.members.flatMap((memberKey) => {
      const category = categoriesByKey.get(memberKey)

      return category == null ? [] : [category]
    })
  }))
}

export const normalizeRiskForecastGraphResponse = (
  result?: IQRARiskForecastGraphApiResult | null
): IQRARiskForecastGraphData => {
  const years = [...(result?.categoriesForecastResult ?? [])].sort(
    (left, right) => left.year - right.year
  )

  const groupingConfig: IQRAThreatChartGroupingConfig[] = [
    ...(result?.groupingConfigResult ?? [])
  ]
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .map((groupingConfigItem) => ({
      ...groupingConfigItem,
      groupings: [...groupingConfigItem.groupings].sort(
        (left, right) => left.sortOrder - right.sortOrder
      )
    }))

  const grouping = groupingConfig[0]

  return {
    chart: {
      graphType: QRAGraphType.RISK_FORECAST,
      items: years.map(({ year, categoriesResult }) => ({
        year,
        items: getRiskForecastItems(categoriesResult, grouping)
      }))
    },
    groupingConfig
  }
}
